import { forwardRef, type HTMLAttributes, type ReactNode, type ComponentType } from 'react';
import { cn } from '../../../utils';

// Accept both LucideIcon and generic icon components for cross-package compatibility
type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  icon?: IconComponent;
  title: string;
  description?: string;
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Heading level of the title. A section's empty state keeps h3; a screen
   * that is nothing but the empty state (an access refusal) passes h1, so the
   * page has its one level-one heading (agion/Agion#3028).
   */
  titleAs?: 'h1' | 'h2' | 'h3';
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      icon: Icon,
      title,
      description,
      action,
      size = 'md',
      titleAs: Title = 'h3',
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: {
        container: 'py-8 px-4',
        iconWrapper: 'h-14 w-14',
        icon: 'h-6 w-6',
        title: 'text-base',
        description: 'text-xs',
      },
      md: {
        container: 'py-12 px-6',
        iconWrapper: 'h-20 w-20',
        icon: 'h-10 w-10',
        title: 'text-lg',
        description: 'text-sm',
      },
      lg: {
        container: 'py-16 px-8',
        iconWrapper: 'h-24 w-24',
        icon: 'h-12 w-12',
        title: 'text-xl',
        description: 'text-base',
      },
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center',
          sizeClasses[size].container,
          className
        )}
        role="status"
        aria-live="polite"
        {...props}
      >
        {Icon && (
          <div
            className={cn(
              'flex items-center justify-center rounded-full bg-muted',
              'mb-4',
              sizeClasses[size].iconWrapper
            )}
          >
            <Icon
              className={cn(
                'text-text-placeholder',
                sizeClasses[size].icon
              )}
              aria-hidden="true"
            />
          </div>
        )}
        <Title
          className={cn(
            'font-semibold text-text-primary mb-2',
            sizeClasses[size].title
          )}
        >
          {title}
        </Title>
        {description && (
          <p
            className={cn(
              // The muted-foreground token keeps the current theme's muted ink and
              // holds 4.5:1 on the page background in both next themes (agion/Agion#3001).
              'text-muted-foreground mb-6 max-w-md',
              sizeClasses[size].description
            )}
          >
            {description}
          </p>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    );
  }
);
EmptyState.displayName = 'EmptyState';
